/**
 * Phase 118C — RelationalMultiSelect
 *
 * Sibling to RelationalSelect that surfaces a multi-checkbox popover
 * for relational entities that can be assigned in multiples
 * (e.g. an HRMS coach assigned to multiple training batches).
 *
 * Bound to the SAME live entity endpoint pattern (`/academy/{collection}`)
 * so inline "+ Add New" stays consistent with the rest of the academy
 * configuration surfaces.
 *
 * Values flow as a string-id array — matches the backend
 * `assigned_batch_ids: List[str]` schema directly.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Loader2, ChevronsUpDown, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export function RelationalMultiSelect({
  values = [],              // string[] of selected ids
  onValuesChange,           // (string[]) => void
  endpoint,                 // e.g. '/academy/batches'
  entityLabel,              // 'Batch'
  placeholder = 'Select…',
  filterFn,                 // optional (opt) => bool
  disabled = false,
  emptyHint,
  extraCreatePayload = {},
  testid,
  className,
}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [open, setOpen] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);

  const fetchOptions = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(endpoint);
      const list = Array.isArray(r.data) ? r.data : [];
      setOptions(list);
    } catch (err) {
      console.error(`${entityLabel} fetch failed`, err);
    } finally {
      setLoading(false);
    }
  }, [endpoint, entityLabel]);

  useEffect(() => { fetchOptions(); }, [fetchOptions]);

  const visibleOptions = useMemo(
    () => (filterFn ? options.filter(filterFn) : options),
    [options, filterFn],
  );

  const valueSet = useMemo(() => new Set(values || []), [values]);
  const selectedOptions = useMemo(
    () => visibleOptions.filter((o) => valueSet.has(o.id)),
    [visibleOptions, valueSet],
  );

  const toggleValue = (id) => {
    if (!onValuesChange) return;
    const next = valueSet.has(id)
      ? (values || []).filter((v) => v !== id)
      : [...(values || []), id];
    onValuesChange(next);
  };

  const removeBadge = (e, id) => {
    e.stopPropagation();
    onValuesChange((values || []).filter((v) => v !== id));
  };

  const clearAll = (e) => {
    e.stopPropagation();
    onValuesChange([]);
  };

  const handleCreate = async () => {
    const trimmed = newName.trim();
    if (!trimmed) { toast.error(`${entityLabel} name is required`); return; }
    setCreating(true);
    try {
      const payload = { name: trimmed, ...extraCreatePayload };
      const r = await api.post(endpoint, payload);
      toast.success(`${entityLabel} "${r.data.name}" created`);
      setOptions((prev) => (prev.find((o) => o.id === r.data.id) ? prev : [...prev, r.data]));
      // Auto-select the newly created entity
      if (r.data?.id) onValuesChange([...(values || []), r.data.id]);
      setNewName('');
      setShowAddForm(false);
      fetchOptions();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to create ${entityLabel}`);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className={className}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            data-testid={testid}
            className="flex h-auto min-h-9 w-full items-start justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 hover:border-emerald-500/50 transition-colors"
          >
            <div className="flex-1 min-w-0 flex flex-wrap gap-1.5 items-center">
              {selectedOptions.length === 0 ? (
                <span className="text-muted-foreground">{placeholder}</span>
              ) : (
                selectedOptions.map((opt) => (
                  <span
                    key={opt.id}
                    data-testid={`${testid}-badge-${opt.id}`}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-emerald-500/10 text-emerald-700 border border-emerald-500/30"
                  >
                    {opt.name}
                    <span
                      role="button"
                      aria-label={`Remove ${opt.name}`}
                      onClick={(e) => removeBadge(e, opt.id)}
                      className="hover:bg-emerald-500/20 rounded p-0.5 -mr-0.5 cursor-pointer"
                    >
                      <X className="h-2.5 w-2.5" />
                    </span>
                  </span>
                ))
              )}
            </div>
            <div className="flex items-center gap-1 ml-2 flex-shrink-0 self-center">
              {selectedOptions.length > 0 && (
                <span
                  role="button"
                  aria-label="Clear all"
                  onClick={clearAll}
                  data-testid={`${testid}-clear`}
                  className="hover:bg-rose-500/20 rounded p-0.5 text-rose-500 cursor-pointer"
                >
                  <X className="h-3.5 w-3.5" />
                </span>
              )}
              <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
            </div>
          </button>
        </PopoverTrigger>

        <PopoverContent
          className="w-[--radix-popover-trigger-width] p-0 min-w-[260px]"
          align="start"
          data-testid={`${testid}-popover`}
        >
          <div className="max-h-72 overflow-y-auto py-1">
            {loading ? (
              <div className="px-3 py-6 text-center text-xs text-muted-foreground flex items-center justify-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
              </div>
            ) : visibleOptions.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-muted-foreground">
                {emptyHint || `No ${entityLabel.toLowerCase()}s yet`}
              </div>
            ) : (
              visibleOptions.map((opt) => {
                const checked = valueSet.has(opt.id);
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => toggleValue(opt.id)}
                    data-testid={`${testid}-option-${opt.id}`}
                    data-checked={checked}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-emerald-500/5 transition-colors ${
                      checked ? 'bg-emerald-500/8' : ''
                    }`}
                  >
                    <Checkbox
                      checked={checked}
                      tabIndex={-1}
                      className="pointer-events-none"
                    />
                    <span className="flex-1 truncate">{opt.name}</span>
                    {checked && <Check className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />}
                  </button>
                );
              })
            )}
          </div>

          <div className="border-t bg-muted/30">
            {showAddForm ? (
              <div className="p-2.5 space-y-2">
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  New {entityLabel} name
                </Label>
                <div className="flex gap-2">
                  <Input
                    autoFocus
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); handleCreate(); }
                      if (e.key === 'Escape') { setShowAddForm(false); setNewName(''); }
                    }}
                    placeholder={`${entityLabel} name`}
                    className="h-8 text-xs"
                    data-testid={`${testid}-newname-input`}
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleCreate}
                    disabled={creating || !newName.trim()}
                    data-testid={`${testid}-create-confirm`}
                    className="h-8 px-3 text-xs bg-emerald-600 hover:bg-emerald-700"
                  >
                    {creating ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Add'}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => { setShowAddForm(false); setNewName(''); }}
                    className="h-8 px-2 text-xs"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowAddForm(true)}
                data-testid={`${testid}-addnew-trigger`}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-emerald-600 hover:bg-emerald-500/5 transition-colors"
              >
                <Plus className="h-3.5 w-3.5" /> Add new {entityLabel.toLowerCase()}
              </button>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
